import { forwardRef, MutableRefObject, useEffect } from 'react';

import { useFocusInputFunctionality } from 'core/hooks';
import { FormProvider } from 'core/states';
import { IImportVendorQuoteForm } from 'quotes/types';

import { IImportContactHandle, ImportContactFormFields } from './ImportContactFormFields';
import { importVendorQuoteSchema } from '../../schema';
import { getContactStepFocusElementName } from '../../utils';

interface IProps {
  defaultValues: Partial<IImportVendorQuoteForm>;
  disabled?: boolean;
  isContactLocked?: boolean;
  isContactPrefilled?: boolean;
  isVendorLocked?: boolean;
  isVendorMapped?: boolean;
  isVendorMappingRemembered?: boolean;
  showVendorLocation?: boolean;
  showVendorQuoteNumber?: boolean;
  isVendorQuoteNumberLocked?: boolean;
  showVendorQuoteRevision?: boolean;
  suggestedContactIdRef?: MutableRefObject<number | null>;
}

export const ImportContact = forwardRef<IImportContactHandle, IProps>(function ImportContact(
  { defaultValues, ...fieldsProps },
  ref
) {
  const { setFocusElementName } = useFocusInputFunctionality();

  const {
    showVendorLocation = false,
    isVendorLocked = false,
    showVendorQuoteNumber = false,
    isVendorQuoteNumberLocked = false,
    showVendorQuoteRevision = false,
  } = fieldsProps;

  const isVendorQuoteNumberRequired = showVendorQuoteNumber && !isVendorQuoteNumberLocked;

  const focusElementName = getContactStepFocusElementName({
    showVendorLocation,
    isVendorLocked,
    showVendorQuoteNumber: isVendorQuoteNumberRequired,
    hasContact: defaultValues?.contact?.id != null,
  });

  useEffect(() => {
    setFocusElementName(focusElementName);
  }, [setFocusElementName, focusElementName]);

  return (
    <FormProvider
      schema={importVendorQuoteSchema({
        isProjectRequired: false,
        isVendorRequired: showVendorLocation,
        isVendorQuoteNumberRequired,
        isVendorQuoteRevisionShown: showVendorQuoteRevision,
      })}
      defaultValues={defaultValues}
    >
      <ImportContactFormFields ref={ref} {...fieldsProps} />
    </FormProvider>
  );
});
